import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Typography,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  Science as ScienceIcon,
  Build as BuildIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HelpOutline as HelpOutlineIcon
} from '@mui/icons-material';
import api from '../../api/axios';

/**
 * Backtest Panel
 * Runs a dry-run of the generated flow against uploaded sample files and shows
 * pass/fail results per expected field. Offers automatic repair when failing.
 * Shown before the user applies/saves a generated flow.
 */
const BacktestPanel = ({ sessionId, flowSteps, onStepsRepaired }) => {
  const [running, setRunning] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [escalationId, setEscalationId] = useState(null);

  const hasSteps = Array.isArray(flowSteps) && flowSteps.length > 0;

  const runBacktest = async () => {
    setRunning(true);
    setError(null);
    setEscalationId(null);
    try {
      const response = await api.post('/ai-flow-builder/backtest', {
        sessionId,
        steps: flowSteps
      });
      setReport(response.data.report);
      setExpanded(true);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setRunning(false);
    }
  };

  const runRepair = async () => {
    setRepairing(true);
    setError(null);
    try {
      const response = await api.post('/ai-flow-builder/repair-from-backtest', {
        sessionId,
        steps: flowSteps,
        maxIterations: 3
      });
      setReport(response.data.report);
      if (response.data.escalationId) {
        setEscalationId(response.data.escalationId);
      }
      if (response.data.flow && onStepsRepaired) {
        onStepsRepaired(response.data.flow);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setRepairing(false);
    }
  };

  const verdictChip = () => {
    if (!report) return null;
    if (report.passed === true) {
      return <Chip icon={<CheckCircleIcon />} label="Passed" color="success" size="small" />;
    }
    if (report.passed === false) {
      return <Chip icon={<CancelIcon />} label="Failed" color="error" size="small" />;
    }
    return <Chip icon={<HelpOutlineIcon />} label="Needs Review" color="warning" size="small" />;
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return 'null';
    const str = typeof value === 'object' ? JSON.stringify(value) : String(value);
    return str.length > 60 ? str.substring(0, 60) + '…' : str;
  };

  if (!hasSteps) return null;

  return (
    <Box sx={{ borderTop: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1 }}>
        <ScienceIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          Backtest against sample files
        </Typography>
        {verdictChip()}
        <Button
          size="small"
          variant="outlined"
          onClick={runBacktest}
          disabled={running || repairing}
          startIcon={running ? <CircularProgress size={14} /> : <ScienceIcon />}
        >
          {running ? 'Running…' : 'Run Backtest'}
        </Button>
        {report?.passed === false && (
          <Button
            size="small"
            variant="contained"
            color="warning"
            onClick={runRepair}
            disabled={running || repairing}
            startIcon={repairing ? <CircularProgress size={14} /> : <BuildIcon />}
          >
            {repairing ? 'Repairing…' : 'Auto-Repair'}
          </Button>
        )}
        {report && (
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mx: 2, mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {escalationId && (
        <Alert severity="warning" sx={{ mx: 2, mb: 1 }}>
          Automatic repair could not fix this flow. An admin escalation with a
          Cursor-ready fix prompt was created (ID: {escalationId}).
        </Alert>
      )}

      {report && (
        <Collapse in={expanded}>
          <Box sx={{ px: 2, pb: 2, maxHeight: 280, overflowY: 'auto' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {report.summary}
            </Typography>
            {report.sampleResults?.map((result, i) => (
              <Box key={i} sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {result.fileName}
                  {result.skipped ? ` — skipped (${result.reason})` : ''}
                </Typography>
                {!result.skipped && result.fieldResults?.length > 0 && (
                  <List dense disablePadding>
                    {result.fieldResults.map((f, j) => (
                      <ListItem key={j} disableGutters sx={{ py: 0 }}>
                        <ListItemText
                          primaryTypographyProps={{ variant: 'caption' }}
                          primary={
                            <span>
                              {f.match ? '✅' : '❌'} <code>{f.variable}</code>
                              {!f.match && (
                                <span>
                                  {' '}— expected <code>{formatValue(f.expected)}</code>, got{' '}
                                  <code>{formatValue(f.actual)}</code>
                                </span>
                              )}
                            </span>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
                {!result.skipped && (!result.fieldResults || result.fieldResults.length === 0) && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Extracted: {Object.keys(result.stepOutputs || {}).join(', ') || 'nothing'} (no
                    golden expectations saved for this file)
                  </Typography>
                )}
                {result.errors?.length > 0 && (
                  <Typography variant="caption" color="error" display="block">
                    Errors: {result.errors.join('; ')}
                  </Typography>
                )}
                <Divider sx={{ mt: 0.5 }} />
              </Box>
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default BacktestPanel;
